import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Tonality } from '@dsny/dsny-component-library'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import OptionActions from 'reducers/Options'
import { isArchivedFilter } from 'utils/archiveFilter'
import { Close, Cancel } from '@dsny/dsny-component-library/icons'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { trackEvent } from 'utils/segmentService'
import { TRACKING_EVENTS, FILTER_ACTIONS, PLATFORMS } from 'constants/tracking'
import {
  ClearAllButtonContainer,
  ActiveFiltersMenuContainer
} from './ActiveFiltersMenu.styles'

const ActiveFiltersMenu = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMobileMediaQuery()
  const { activeFilters } = useSelector((state) => state.options)

  const isThereAnyFilterSelected = activeFilters.length > 0

  const clearAllFilters = () => {
    const totalActiveFilters = String(activeFilters.length)
    const eventData = {
      action: FILTER_ACTIONS.CLEAR_ALL,
      filter_type: 'all',
      filter_label: 'all_filters',
      platform: isMobile ? PLATFORMS.MOBILE : PLATFORMS.DESKTOP,
      total_active_filters: totalActiveFilters
    }

    // Track with Segment (and Statsig via statsigCustom property)
    trackEvent(TRACKING_EVENTS.FILTER_INTERACTION, eventData)

    dispatch(OptionActions.clearAllFilters())
  }

  const handleRemoveFilter = (filter) => {
    const totalActiveFilters = String(activeFilters.length)
    const eventData = {
      action: FILTER_ACTIONS.REMOVE,
      filter_type: filter.type ? String(filter.type) : '',
      filter_label: filter.label ? String(filter.label) : '',
      platform: isMobile ? PLATFORMS.MOBILE : PLATFORMS.DESKTOP,
      total_active_filters: totalActiveFilters
    }

    // Track with Segment (and Statsig via statsigCustom property)
    trackEvent(TRACKING_EVENTS.FILTER_INTERACTION, eventData)

    dispatch(OptionActions.toggleFilter(filter))
  }

  const renderClearAllButton = () => (
    <ClearAllButtonContainer onClick={clearAllFilters}>
      <span>{t('home.filter-clear-all')}</span>
      <Cancel
        color={theme.colors.neutralW90}
        style={{
          width: 16,
          height: 16
        }}
      />
    </ClearAllButtonContainer>
  )

  // Nothing to show: return null so the filters section gap isn't applied
  // to an empty container.
  if (!isThereAnyFilterSelected) {
    return null
  }

  if (isMobile) {
    return renderClearAllButton()
  }

  // Desktop: show filter pills + clear all
  return (
    <ActiveFiltersMenuContainer>
      {activeFilters.map((filter) => (
        <Tonality.Pill
          key={`${filter.type}-${filter.id}`}
          style={{
            borderRadius: 100,
            padding: '4px 8px 4px 10px'
          }}
          variant="tonal"
          mode="dark"
          label={
            isArchivedFilter(filter)
              ? t('home.archived-releases')
              : filter.label
          }
          iconPosition="tailing"
          onIconClick={() => handleRemoveFilter(filter)}
          icon={<Close width={16} height={16} />}
        />
      ))}
      {isThereAnyFilterSelected && renderClearAllButton()}
    </ActiveFiltersMenuContainer>
  )
}

export default ActiveFiltersMenu
